import {
	GridToolbarContainer,
	GridToolbarExport,
	GridColDef,
	GridToolbarColumnsButton,
} from '@mui/x-data-grid';
import { Box, IconButton } from '@mui/material';
import React, { useState } from 'react';
import { styled } from 'styled-components';
import { HeaderContainer, HeaderTitle } from './layout/PageHeader';
import SearchBar from './SearchBar';
import AddIcon from '@mui/icons-material/Add';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { PaperHeader } from './layout/Paper';
import { TableFilterOption } from './Table';
import Autocomplete from './form/AutoComplete';

interface TableToolbarProps {
	title?: string;
	searchText?: string;
	handleTextSearch?: (localSearchText: string) => void;
	handleAdd?: () => void;
	filterOptions?: TableFilterOption[];
	headerButtons?: React.ReactNode[];
	bulkActions?: React.ReactNode;
	selectedCount?: number;
	// Column visibility props
	columns?: GridColDef[];
	columnVisibilityModel?: Record<string, boolean>;
	onColumnVisibilityModelChange?: (model: Record<string, boolean>) => void;
	enableColumnVisibilityToggle?: boolean;
}

const TableToolbar: React.FC<TableToolbarProps> = (
	props: TableToolbarProps
) => {
	const {
		title = '',
		searchText = '',
		handleTextSearch,
		handleAdd,
		filterOptions,
		headerButtons,
		bulkActions,
		selectedCount = 0,
		// Column visibility props
		columns = [],
		columnVisibilityModel = {},
		onColumnVisibilityModelChange,
		enableColumnVisibilityToggle = false,
	} = props;

	const [localSearchText, setLocalSearchText] = useState(searchText);
	// Using built-in DataGrid columns button; no local anchor state needed

	const handleSearch = () => {
		handleTextSearch && handleTextSearch(localSearchText);
	};

	const handleClear = () => {
		setLocalSearchText('');
		handleTextSearch && handleTextSearch('');
	};

	return (
		<StyledGridToolbarContainer>
			<HeaderContainer>
				{title && <TableHeader>{title}</TableHeader>}
				{handleAdd && (
					<IconButtonContainer>
						<IconButton onClick={handleAdd} color={'primary'}>
							<AddIcon />
						</IconButton>
					</IconButtonContainer>
				)}
				<HeaderRightContainer>
					{!!handleTextSearch && (
						<Box
							sx={{
								width: 300,
								marginLeft: 2,
								marginRight: 2,
							}}
						>
							<SearchBar
								searchText={localSearchText}
								setSearchText={setLocalSearchText}
								handleTextSearch={handleSearch}
								handleClear={handleClear}
							/>
						</Box>
					)}
					{/* <GridToolbarExport /> */}
					{headerButtons?.map((button, index) => (
						<React.Fragment key={index}>{button}</React.Fragment>
					))}
				</HeaderRightContainer>
			</HeaderContainer>
			{bulkActions && (
				<BulkActionsContainer>
					{enableColumnVisibilityToggle && (
						<Box sx={{ marginRight: 2 }}>
							<GridToolbarColumnsButton />
						</Box>
					)}
					{bulkActions}
					{/* <BulkActionsText>
						{selectedCount > 0
							? `${selectedCount} item${
									selectedCount !== 1 ? 's' : ''
							  } selected`
							: ''}
					</BulkActionsText> */}
				</BulkActionsContainer>
			)}
			<FiltersContainer>
				{filterOptions?.map((filterOption) => (
					<AutoCompleteContainer key={filterOption.field}>
						<Autocomplete
							key={filterOption.field}
							label={filterOption.label}
							value={filterOption.value}
							options={filterOption.options}
							onChange={(event, value) => {
								filterOption.onChange(
									filterOption.field,
									value
								);
							}}
							InputLabelProps={{ shrink: !!filterOption.value }}
						/>
					</AutoCompleteContainer>
				))}
			</FiltersContainer>
		</StyledGridToolbarContainer>
	);
};

export const TableHeader = styled.div`
	color: ${(props) => props.theme.palette.text.label};
	margin-left: 10px;
	margin-top: 10px;
	margin-bottom: 10px;
	display: flex;
	align-items: center;
	font-weight: normal;
	font-size: 24px;
`;

const StyledGridToolbarContainer = styled(GridToolbarContainer)`
	display: block !important;
	padding: 10px 10px 0 10px !important;
`;

const HeaderRightContainer = styled.div`
	flex: 1;
	display: flex;
	justify-content: flex-end;
`;

const IconButtonContainer = styled.div`
	margin-left: 10px;
`;

const FiltersContainer = styled.div`
	display: flex;
	align-items: center;
	margin-top: 10px;
`;

const AutoCompleteContainer = styled.div`
	width: 175px;
	margin-right: 10px;
`;

const BulkActionsContainer = styled.div`
	display: flex;
	align-items: center;
	background-color: ${(props) => props.theme.palette.primary.light}20;
	border-radius: 4px;
	gap: 16px;
`;

const BulkActionsText = styled.div`
	color: ${(props) => props.theme.palette.primary.main};
	font-weight: 500;
	font-size: 14px;
`;

export default TableToolbar;
